"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Code as Code2, Sparkles, Waves } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function () {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  const pillars = [{
    number: "01",
    eyebrow: "PHASE 01",
    title: "Growth Strategy",
    description: "We engineer high-velocity roadmaps. Data-backed positioning and narrative systems designed to capture market share and maximize ROI.",
    services: ["MARKET POSITIONING", "ROI ARCHITECTURE", "BRAND NARRATIVE", "CONVERSION SYSTEMS", "COMPETITIVE ANALYSIS"],
    Icon: Sparkles,
    gradient: "radial-gradient(ellipse at 20% 30%, hsl(22 100% 50% / 0.15), transparent 55%), radial-gradient(ellipse at 80% 70%, hsl(240 10% 4% / 0.12), transparent 60%)"
  }, {
    number: "02",
    eyebrow: "PHASE 02",
    title: "Performance Tech",
    description: "High-performance digital infrastructure built for scale. Code that converts, optimized for speed and seamless user journeys.",
    services: ["FULL-STACK DEVELOPMENT", "CONVERSION OPTIMIZATION", "E-COMMERCE SCALING", "TECHNICAL SEO", "API INTEGRATION"],
    Icon: Code2,
    gradient: "radial-gradient(ellipse at 25% 25%, hsl(22 100% 50% / 0.15), transparent 55%), radial-gradient(ellipse at 75% 75%, hsl(222 47% 11% / 0.12), transparent 60%)"
  }, {
    number: "03",
    eyebrow: "PHASE 03",
    title: "Impact Creative",
    description: "Visual assets that command attention. Cinematic motion and high-impact design that turns passive viewers into active customers.",
    services: ["CAMPAIGN ASSETS", "PRODUCT VISUALIZATION", "UI/UX MOTION", "AD CREATIVE", "BRANDED CONTENT"],
    Icon: Waves,
    gradient: "radial-gradient(ellipse at 30% 30%, hsl(22 100% 50% / 0.18), transparent 55%), radial-gradient(ellipse at 75% 70%, hsl(240 6% 10% / 0.18), transparent 60%)"
  }];
  useEffect(() => {
    const container = containerRef.current;
    const track = trackRef.current;
    if (!container || !track) return;
    const isMobile = () => window.matchMedia("(max-width: 1023px)").matches;
    const onScroll = () => {
      if (isMobile()) {
        setProgress(0);
        track.style.transform = "translate3d(0,0,0)";
        return;
      }
      const rect = container.getBoundingClientRect();
      const total = container.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const p = total > 0 ? scrolled / total : 0;
      setProgress(p);
      const maxTranslate = track.scrollWidth - window.innerWidth;
      track.style.transform = `translate3d(${-p * maxTranslate}px, 0, 0)`;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  return <section data-section-id="3720" id="growth-solutions" className="bg-background text-foreground relative">
      {}
      <div className="px-6 md:px-12 lg:px-20 pt-24 pb-16 md:pt-32 md:pb-24">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-primary mb-8">
            03 &mdash; The Catalyst Engine
          </p>
          <h2 className="font-default font-bold text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter mb-8">
            Three phases.
            <br />
            <span className="italic text-primary">Total growth.</span>
          </h2>
          <p className="font-default text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            A high-velocity performance engine engineered to transform brands &mdash;
            strategy, tech, and creative working as a single, unstoppable force.
          </p>
        </div>
      </div>

      {}
      <div ref={containerRef} className="hidden lg:block relative" style={{
      height: `${pillars.length * 100}vh`
    }}>
        <div className="sticky top-0 h-screen w-full overflow-hidden">
          {}
          <div className="absolute top-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground mr-2">
              Scroll
            </span>
            {pillars.map((_, i) => {
            const segment = 1 / pillars.length;
            const fill = Math.min(Math.max((progress - i * segment) / segment, 0), 1);
            return <div key={i} data-index={i} className="relative h-[2px] w-12 bg-border overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-primary transition-[width] duration-150" style={{
                width: `${fill * 100}%`
              }} />
                </div>;
          })}
          </div>

          {}
          <div ref={trackRef} className="flex h-full will-change-transform" style={{
          width: `${pillars.length * 100}vw`
        }}>
            {pillars.map((p, i) => {
            const Icon = p.Icon;
            return <article key={p.number} data-index={i} className="relative h-full w-screen shrink-0 flex items-center justify-center px-12 xl:px-24">
                  <div className="relative w-full max-w-7xl h-[78vh] border border-border bg-card overflow-hidden" style={{
                backgroundImage: p.gradient
              }}>
                    {}
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-transparent via-primary to-transparent" />

                    {}
                    <div className="absolute top-4 left-8 font-mono font-bold leading-none select-none pointer-events-none text-primary/10 text-[12rem] xl:text-[16rem]">
                      {p.number}
                    </div>

                    {}
                    <div className="relative h-full grid grid-cols-12 gap-8 p-12 xl:p-16">
                      <div className="col-span-7 flex flex-col justify-between">
                        <div>
                          <p className="font-mono text-xs uppercase tracking-widest text-primary mb-6">
                            {p.eyebrow}
                          </p>
                          <Icon className="w-12 h-12 text-foreground mb-10" strokeWidth={1} />
                          <h3 className="font-default font-bold text-5xl xl:text-7xl leading-[0.95] tracking-tighter mb-8">
                            {p.title}
                          </h3>
                          <p className="font-default text-base xl:text-lg text-muted-foreground max-w-xl leading-relaxed">
                            {p.description}
                          </p>
                        </div>
                      </div>

                      <div className="col-span-5 flex flex-col justify-between items-end">
                        <ul className="w-full space-y-3 mt-24">
                          {p.services.map((s, idx) => <li key={s} data-index={idx} className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-foreground/80 border-b border-border pb-3">
                              <ArrowUpRight className="w-3.5 h-3.5 text-primary" />
                              <span>{s}</span>
                            </li>)}
                        </ul>

                        <Link to="/ContactPage" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-foreground hover:text-primary transition-colors mt-8">
                          <span>Start {p.title}</span>
                          <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </article>;
          })}
          </div>
        </div>
      </div>

      {}
      <div className="lg:hidden px-6 md:px-12 pb-24 space-y-8">
        {pillars.map((p, i) => {
        const Icon = p.Icon;
        return <article key={p.number} data-index={i} className="relative border border-border bg-card overflow-hidden" style={{
          backgroundImage: p.gradient
        }}>
              <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-primary to-transparent" />
              <div className="absolute top-2 left-4 font-mono font-bold leading-none select-none pointer-events-none text-primary/10 text-[8rem]">
                {p.number}
              </div>
              <div className="relative p-8 md:p-12">
                <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">
                  {p.eyebrow}
                </p>
                <Icon className="w-10 h-10 text-foreground mb-6" strokeWidth={1} />
                <h3 className="font-default font-bold text-4xl md:text-5xl leading-[0.95] tracking-tighter mb-5">
                  {p.title}
                </h3>
                <p className="font-default text-base text-muted-foreground leading-relaxed mb-8">
                  {p.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {p.services.map((s, idx) => <li key={s} data-index={idx} className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-foreground/80 border-b border-border pb-3">
                      <ArrowUpRight className="w-3.5 h-3.5 text-primary" />
                      <span>{s}</span>
                    </li>)}
                </ul>
                <Button asChild variant="ghost" className="font-mono text-xs uppercase tracking-widest px-0 hover:bg-transparent hover:text-primary">
                  <Link to="/ContactPage" className="inline-flex items-center gap-2">
                    <span>Start {p.title}</span>
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </article>;
      })}
      </div>
    </section>;
}